import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const Backdrop = styled(motion.div, {
  base: {
    position: 'fixed',
    inset: '0',
    zIndex: '100',
    backgroundColor: 'rgba(26, 19, 48, 0.55)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    overflowY: 'auto',
  },
})

const Card = styled(motion.div, {
  base: {
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    boxShadow: '0 32px 80px -24px rgba(128, 84, 255, 0.42)',
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const Header = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '24px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.4px',
  },
})

const Subtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

const Form = styled('form', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const FieldGroup = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const FieldRow = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
  },
})

const SectionTitle = styled('h3', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '4px 0 0 0',
  },
})

const ResidenceCard = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    borderRadius: 'md',
    border: '1px solid',
    borderColor: '#EAE6F5',
  },
})

const ResidenceHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
})

const ResidenceLabel = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const RemoveButton = styled('button', {
  base: {
    background: 'none',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9A2222',
    textDecoration: 'underline',
    _hover: { opacity: '0.75' },
  },
})

const AddButton = styled('button', {
  base: {
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: 'quickpass.primary',
    textDecoration: 'underline',
    _hover: { opacity: '0.75' },
  },
})

const FieldLabel = styled('label', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
})

const Select = styled('select', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    padding: '10px 12px',
    borderRadius: 'sm',
    border: '1px solid',
    borderColor: '#D9D2EC',
    backgroundColor: '#FFFFFF',
    width: '100%',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
    },
  },
})

const ConsentBox = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px',
    borderRadius: 'md',
    backgroundColor: '#F7F5FF',
    border: '1px solid',
    borderColor: '#E3DCF7',
  },
})

const ConsentText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.55',
  },
})

const ConsentLabel = styled('label', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.ink',
    cursor: 'pointer',
    lineHeight: '1.45',
  },
})

const ConsentCheckbox = styled('input', {
  base: {
    marginTop: '2px',
    width: '18px',
    height: '18px',
    flexShrink: '0',
    cursor: 'pointer',
    accentColor: '#8054FF',
  },
})

const PriceBox = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    borderRadius: 'md',
    border: '1px solid',
    borderColor: '#EAE6F5',
    backgroundColor: '#FBFAFF',
  },
})

const PriceRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
  },
})

const PriceTotalRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    paddingTop: '8px',
    borderTop: '1px solid',
    borderColor: '#EAE6F5',
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const CostNote = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.45',
  },
})

const ErrorBanner = styled('div', {
  base: {
    paddingX: '14px',
    paddingY: '10px',
    borderRadius: 'sm',
    fontFamily: 'body',
    fontSize: '13px',
    lineHeight: '1.45',
    backgroundColor: '#FCE2E2',
    color: '#9A2222',
  },
})

const Footer = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
  },
})

const PaymentStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const PaymentElementSlot = styled('div', {
  base: {
    minHeight: '180px',
  },
})

const PaymentLoadingNote = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    textAlign: 'center',
    paddingY: '24px',
  },
})

export {
  Backdrop,
  Card,
  Header,
  Title,
  Subtitle,
  Form,
  FieldGroup,
  FieldRow,
  SectionTitle,
  ResidenceCard,
  ResidenceHeader,
  ResidenceLabel,
  RemoveButton,
  AddButton,
  FieldLabel,
  Select,
  ConsentBox,
  ConsentText,
  ConsentLabel,
  ConsentCheckbox,
  PriceBox,
  PriceRow,
  PriceTotalRow,
  CostNote,
  ErrorBanner,
  Footer,
  PaymentStack,
  PaymentElementSlot,
  PaymentLoadingNote,
}
